import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { BranchOperationalRow } from "../../types/dashboard.type";
import { BRANCH_ANALYSIS_RADIUS } from "../../constants/branchAnalysis.styles";
import { CONTENT_FONT_FAMILY_VAR } from "../../constants/fonts";
import PageTitleIcon from "../PageTitleIcon";
import { SetupCoachmark } from "../selfSetup/SetupCoachmark";
import { buildBranchComparisonPdf } from "../../utils/branchComparisonPdf.helpers";
import { CONTENT_CHART_COLORS } from "../../constants/contentColors";

const UNASSIGNED_SITE_ID = "__unassigned__";
const MIN_COMPARE = 2;
const CHART_COLORS = CONTENT_CHART_COLORS.light;

type CompareStep = "select" | "compare";

function safeNum(value: number | null | undefined): number {
  if (value == null || Number.isNaN(Number(value))) return 0;
  return Number(value);
}

function fmtNum(n: number | null | undefined, decimals = 2): string {
  return safeNum(n).toLocaleString(undefined, { maximumFractionDigits: decimals });
}

function branchLabel(
  branch: BranchOperationalRow,
  t: (key: string) => string,
  isUnassigned: boolean,
): string {
  if (isUnassigned) {
    return t("branchAnalysis.unassignedBranch");
  }
  return branch.name?.trim() || t("branchAnalysis.unnamedBranch");
}

function truncateLabel(label: string, max = 16): string {
  return label.length > max ? `${label.slice(0, max)}…` : label;
}

interface CompareMetric {
  key: string;
  label: string;
  format: (row: BranchOperationalRow) => string;
  barValue?: (row: BranchOperationalRow) => number;
}

interface BranchChartRow {
  id: string;
  name: string;
  fullName: string;
  wasteDiverted: number;
  fillReports: number;
  diversionRate: number;
}

export type BranchCompareTourPhase = "select" | "results" | "export";

export interface BranchComparisonDialogProps {
  open: boolean;
  onClose: () => void;
  branches: BranchOperationalRow[];
  organizationName: string;
  organizationLogoUrl: string | null;
  selectedMonth: string;
  /** Self-setup / help tour overlay inside the dialog. */
  tourGuide?: {
    active: boolean;
    phase: BranchCompareTourPhase;
    onDismiss: () => void;
    onNext: () => void;
    selectTitle: string;
    selectSubtitle: string;
    resultsTitle: string;
    resultsSubtitle: string;
    exportTitle: string;
    exportSubtitle: string;
    nextLabel: string;
    doneLabel: string;
  } | null;
}

const BranchComparisonDialog: React.FC<BranchComparisonDialogProps> = ({
  open,
  onClose,
  branches,
  organizationName,
  organizationLogoUrl,
  selectedMonth,
  tourGuide = null,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [step, setStep] = useState<CompareStep>("select");
  const [isExporting, setIsExporting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const compareMetrics: CompareMetric[] = useMemo(
    () => [
      {
        key: "waste_diverted",
        label: t("branchAnalysis.metrics.totalWaste"),
        format: (row) => `${fmtNum(row.total_waste_kg)} kg`,
        barValue: (row) => safeNum(row.total_waste_kg),
      },
      {
        key: "fill_reports",
        label: t("branchAnalysis.metrics.fillReports"),
        format: (row) => safeNum(row.fill_level_report_count).toLocaleString(),
        barValue: (row) => safeNum(row.fill_level_report_count),
      },
      {
        key: "diversion",
        label: t("branchAnalysis.metrics.diversionRate"),
        format: (row) => `${fmtNum(row.waste_diversion_rate_percent, 1)}%`,
        barValue: (row) => safeNum(row.waste_diversion_rate_percent),
      },
    ],
    [t],
  );

  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set());
      setStep("select");
      return;
    }
    // Prefill two branches so the tour can advance to results.
    if (tourGuide?.active && branches.length >= MIN_COMPARE) {
      setSelectedIds(
        new Set(branches.slice(0, MIN_COMPARE).map((b) => b.collection_site_id)),
      );
    }
  }, [open, tourGuide?.active, branches]);

  useEffect(() => {
    if (!open || !tourGuide?.active) return;
    if (
      (tourGuide.phase === "results" || tourGuide.phase === "export") &&
      step !== "compare"
    ) {
      setStep("compare");
    }
    if (tourGuide.phase === "select" && step !== "select") {
      setStep("select");
    }
  }, [open, tourGuide?.active, tourGuide?.phase, step]);

  const selectedBranches = useMemo(
    () => branches.filter((b) => selectedIds.has(b.collection_site_id)),
    [branches, selectedIds],
  );

  const chartData: BranchChartRow[] = useMemo(
    () =>
      selectedBranches.map((branch) => {
        const isUnassigned = branch.collection_site_id === UNASSIGNED_SITE_ID;
        const fullName = branchLabel(branch, t, isUnassigned);
        return {
          id: branch.collection_site_id,
          name: truncateLabel(fullName),
          fullName,
          wasteDiverted: safeNum(branch.total_waste_kg),
          fillReports: safeNum(branch.fill_level_report_count),
          diversionRate: safeNum(branch.waste_diversion_rate_percent),
        };
      }),
    [selectedBranches, t],
  );

  const toggleBranch = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === branches.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(branches.map((b) => b.collection_site_id)));
    }
  };

  const canCompare = selectedIds.size >= MIN_COMPARE;
  const activeStep = step === "select" ? 0 : 1;

  const handleExportPdf = async () => {
    if (!canCompare || selectedBranches.length === 0) return;

    setIsExporting(true);
    try {
      const branchNames = selectedBranches.map((branch) => {
        const isUnassigned = branch.collection_site_id === UNASSIGNED_SITE_ID;
        return branchLabel(branch, t, isUnassigned);
      });

      const { doc, filename } = await buildBranchComparisonPdf({
        organizationName,
        organizationLogoUrl,
        selectedMonth,
        branches: selectedBranches,
        branchNames,
        labels: {
          reportTitle: t("branchAnalysis.compare.export.reportTitle"),
          periodPrefix: t("branchAnalysis.compare.export.periodPrefix"),
          allTimeLabel: t("branchAnalysis.allTime"),
          generatedPrefix: t("branchAnalysis.compare.export.generatedPrefix"),
          branchesCompared: t("branchAnalysis.compare.export.branchesCompared"),
          branchName: t("branchAnalysis.compare.export.branchName"),
          siteId: t("branchAnalysis.compare.export.siteId"),
          detailedComparison: t("branchAnalysis.compare.resultsTitle"),
          visualComparison: t("branchAnalysis.compare.export.visualComparison"),
          metricColumn: t("branchAnalysis.compare.metric"),
          wasteChartTitle: t("branchAnalysis.compare.charts.wasteTitle"),
          fillReportsChartTitle: t("branchAnalysis.compare.charts.fillReportsTitle"),
          diversionChartTitle: t("branchAnalysis.compare.charts.diversionTitle"),
          totalWaste: t("branchAnalysis.metrics.totalWaste"),
          completedWaste: t("branchAnalysis.metrics.completedWaste"),
          fillReports: t("branchAnalysis.metrics.fillReports"),
          diversionRate: t("branchAnalysis.metrics.diversionRate"),
          footerText: t("branchAnalysis.compare.export.footerText"),
        },
      });

      doc.save(filename);
      setSnackbar({
        open: true,
        message: t("branchAnalysis.compare.export.success"),
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: t("branchAnalysis.compare.export.error"),
        severity: "error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const maxByMetric = useMemo(() => {
    const max: Record<string, number> = {};
    for (const metric of compareMetrics) {
      if (!metric.barValue) continue;
      max[metric.key] = Math.max(
        0,
        ...selectedBranches.map((row) => metric.barValue!(row)),
      );
    }
    return max;
  }, [compareMetrics, selectedBranches]);

  const chartTooltipStyle = {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px",
  };

  const renderBranchSelectCard = (branch: BranchOperationalRow) => {
    const isUnassigned = branch.collection_site_id === UNASSIGNED_SITE_ID;
    const label = branchLabel(branch, t, isUnassigned);
    const selected = selectedIds.has(branch.collection_site_id);

    return (
      <Grid item xs={12} sm={6} md={4} key={branch.collection_site_id}>
        <Paper
          elevation={0}
          onClick={() => toggleBranch(branch.collection_site_id)}
          sx={{
            p: 2,
            height: "100%",
            cursor: "pointer",
            position: "relative",
            borderRadius: BRANCH_ANALYSIS_RADIUS,
            border: 2,
            borderColor: selected ? "primary.main" : "divider",
            bgcolor: selected
              ? alpha(theme.palette.primary.main, 0.06)
              : theme.palette.background.paper,
            transition: "border-color 0.2s, background-color 0.2s, box-shadow 0.2s",
            "&:hover": {
              borderColor: selected
                ? "primary.main"
                : alpha(theme.palette.text.primary, 0.25),
              boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.06)}`,
            },
          }}
        >
          {selected && (
            <CheckCircleIcon
              color="primary"
              sx={{ position: "absolute", top: 10, right: 10, fontSize: 22 }}
            />
          )}

          <Typography variant="subtitle1" fontWeight={700} sx={{ pr: 3 }} noWrap title={label}>
            {label}
          </Typography>

          {!isUnassigned && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "monospace", wordBreak: "break-all", display: "block", mt: 0.25 }}
            >
              {branch.collection_site_id}
            </Typography>
          )}

          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Stack spacing={0.75}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("branchAnalysis.metrics.totalWaste")}
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  {fmtNum(branch.total_waste_kg)} kg
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("branchAnalysis.metrics.diversionRate")}
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  {fmtNum(branch.waste_diversion_rate_percent, 1)}%
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Grid>
    );
  };

  const showSelectTour = Boolean(
    tourGuide?.active && tourGuide.phase === "select",
  );
  const showResultsTour = Boolean(
    tourGuide?.active && tourGuide.phase === "results",
  );
  const showExportTour = Boolean(
    tourGuide?.active && tourGuide.phase === "export",
  );

  useEffect(() => {
    if (!open || !showExportTour) return;
    window.requestAnimationFrame(() => {
      document
        .getElementById("self-setup-branch-compare-export")
        ?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
    });
  }, [open, showExportTour]);

  const renderSelectStep = () => {
    const body = (
      <>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            {t("branchAnalysis.compare.selectBranches")}
            {selectedIds.size > 0 && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({t("branchAnalysis.compare.selectedCount", { count: selectedIds.size })})
              </Typography>
            )}
          </Typography>
          <Button size="small" onClick={handleSelectAll}>
            {selectedIds.size === branches.length
              ? t("branchAnalysis.compare.clearAll")
              : t("branchAnalysis.compare.selectAll")}
          </Button>
        </Stack>

        {branches.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("branchAnalysis.empty")}
          </Typography>
        ) : (
          <Grid container spacing={2} id="self-setup-branch-compare-select">
            {branches.map(renderBranchSelectCard)}
          </Grid>
        )}

        {!canCompare && selectedIds.size > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            {t("branchAnalysis.compare.minBranches", { count: MIN_COMPARE })}
          </Typography>
        )}
      </>
    );

    if (!tourGuide || !showSelectTour) return body;

    return (
      <SetupCoachmark
        open
        title={tourGuide.selectTitle}
        subtitle={tourGuide.selectSubtitle}
        onDismiss={tourGuide.onDismiss}
        onNext={tourGuide.onNext}
        nextLabel={tourGuide.nextLabel}
        fullWidth
      >
        <Box sx={{ width: "100%" }}>{body}</Box>
      </SetupCoachmark>
    );
  };

  const renderCompareStep = () => {
    const body = (
    <Stack spacing={3} id="self-setup-branch-compare-results">
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: BRANCH_ANALYSIS_RADIUS,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          {t("branchAnalysis.compare.charts.wasteTitle")}
        </Typography>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
            <YAxis stroke={theme.palette.text.secondary} tickFormatter={(v) => fmtNum(v, 0)} />
            <Tooltip
              contentStyle={chartTooltipStyle}
              labelFormatter={(_, payload) =>
                payload?.[0]?.payload?.fullName ?? ""
              }
              formatter={(value: number, name: string) => [
                `${fmtNum(value)} kg`,
                name,
              ]}
            />
            <Bar
              dataKey="wasteDiverted"
              name={t("branchAnalysis.metrics.totalWaste")}
              fill={theme.palette.primary.main}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              height: "100%",
              border: 1,
              borderColor: "divider",
              borderRadius: BRANCH_ANALYSIS_RADIUS,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              {t("branchAnalysis.compare.charts.fillReportsTitle")}
            </Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
                <YAxis stroke={theme.palette.text.secondary} allowDecimals={false} />
                <Tooltip
                  contentStyle={chartTooltipStyle}
                  labelFormatter={(_, payload) =>
                    payload?.[0]?.payload?.fullName ?? ""
                  }
                />
                <Bar
                  dataKey="fillReports"
                  name={t("branchAnalysis.metrics.fillReports")}
                  fill={theme.palette.info.main}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              height: "100%",
              border: 1,
              borderColor: "divider",
              borderRadius: BRANCH_ANALYSIS_RADIUS,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              {t("branchAnalysis.compare.charts.diversionTitle")}
            </Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
                <YAxis
                  stroke={theme.palette.text.secondary}
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  contentStyle={chartTooltipStyle}
                  labelFormatter={(_, payload) =>
                    payload?.[0]?.payload?.fullName ?? ""
                  }
                  formatter={(value: number) => [`${fmtNum(value, 1)}%`, t("branchAnalysis.metrics.diversionRate")]}
                />
                <Bar
                  dataKey="diversionRate"
                  name={t("branchAnalysis.metrics.diversionRate")}
                  fill={theme.palette.success.main}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <Box>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          {t("branchAnalysis.compare.resultsTitle")}
        </Typography>
        <TableContainer
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: BRANCH_ANALYSIS_RADIUS,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>
                  {t("branchAnalysis.compare.metric")}
                </TableCell>
                {selectedBranches.map((branch, index) => {
                  const isUnassigned =
                    branch.collection_site_id === UNASSIGNED_SITE_ID;
                  return (
                    <TableCell key={branch.collection_site_id} align="right">
                      <Typography variant="caption" fontWeight={700} display="block">
                        {branchLabel(branch, t, isUnassigned)}
                      </Typography>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: CHART_COLORS[index % CHART_COLORS.length],
                          display: "inline-block",
                          mt: 0.5,
                        }}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {compareMetrics.map((metric) => (
                <TableRow key={metric.key}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                    {metric.label}
                  </TableCell>
                  {selectedBranches.map((branch) => {
                    const barMax = maxByMetric[metric.key] ?? 0;
                    const barVal = metric.barValue?.(branch) ?? 0;
                    const pct = barMax > 0 ? (barVal / barMax) * 100 : 0;
                    return (
                      <TableCell key={branch.collection_site_id} align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {metric.format(branch)}
                        </Typography>
                        {metric.barValue && barMax > 0 && (
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                              mt: 0.75,
                              height: 4,
                              borderRadius: 0.5,
                              bgcolor: alpha(theme.palette.text.primary, 0.08),
                            }}
                          />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
    );

    if (!tourGuide || !showResultsTour) return body;

    return (
      <SetupCoachmark
        open
        title={tourGuide.resultsTitle}
        subtitle={tourGuide.resultsSubtitle}
        onDismiss={tourGuide.onDismiss}
        onNext={tourGuide.onNext}
        nextLabel={tourGuide.nextLabel}
        fullWidth
      >
        <Box sx={{ width: "100%" }}>{body}</Box>
      </SetupCoachmark>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={step === "compare" ? "lg" : "md"}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          border: `1px solid ${alpha(
            theme.palette.primary.main,
            theme.palette.mode === "dark" ? 0.28 : 0.12,
          )}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 24px 64px ${alpha("#000", 0.55)}`
              : `0 20px 50px ${alpha("#0f172a", 0.16)}`,
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          position: "relative",
          px: { xs: 2.5, sm: 3 },
          pt: 2.75,
          pb: 2.25,
          fontSize: "inherit",
          fontWeight: "inherit",
          lineHeight: "inherit",
          letterSpacing: "inherit",
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.28)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 55%, transparent 100%)`
              : `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 55%, ${theme.palette.background.paper} 100%)`,
          borderBottom: `1px solid ${alpha(
            theme.palette.primary.main,
            theme.palette.mode === "dark" ? 0.22 : 0.1,
          )}`,
        }}
      >
        <IconButton
          aria-label={t("common.close")}
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            insetInlineEnd: 12,
            color: "text.secondary",
            bgcolor: alpha(
              theme.palette.common.black,
              theme.palette.mode === "dark" ? 0.2 : 0.04,
            ),
            "&:hover": {
              bgcolor: alpha(
                theme.palette.common.black,
                theme.palette.mode === "dark" ? 0.32 : 0.08,
              ),
            },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            pr: 5,
          }}
        >
          <PageTitleIcon icon={<CompareArrowsIcon />} size={48} />
          <Box sx={{ minWidth: 0, pt: 0.25 }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: CONTENT_FONT_FAMILY_VAR,
                fontWeight: 800,
                fontSize: { xs: "1.2rem", sm: "1.35rem" },
                letterSpacing: "-0.02em",
                lineHeight: 1.25,
                color: "text.primary",
              }}
            >
              {t("branchAnalysis.compare.title")}
            </Typography>
            <Typography
              component="p"
              sx={{
                fontFamily: CONTENT_FONT_FAMILY_VAR,
                mt: 0.5,
                fontWeight: 500,
                fontSize: "0.875rem",
                lineHeight: 1.45,
                color: "text.secondary",
              }}
            >
              {step === "compare"
                ? t("branchAnalysis.compare.compareSubtitle", {
                    count: selectedBranches.length,
                  })
                : t("branchAnalysis.compare.subtitle")}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          <Step>
            <StepLabel>{t("branchAnalysis.compare.stepSelect")}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t("branchAnalysis.compare.stepCompare")}</StepLabel>
          </Step>
        </Stepper>
      </Box>

      <DialogContent dividers>
        {step === "select" ? renderSelectStep() : renderCompareStep()}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === "compare" ? (
          <>
            <Button onClick={() => setStep("select")}>{t("common.back")}</Button>
            <Box sx={{ flex: 1 }} />
            {tourGuide && showExportTour ? (
              <SetupCoachmark
                open
                title={tourGuide.exportTitle}
                subtitle={tourGuide.exportSubtitle}
                onDismiss={tourGuide.onDismiss}
                onNext={tourGuide.onNext}
                nextLabel={tourGuide.doneLabel}
              >
                <Button
                  id="self-setup-branch-compare-export"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleExportPdf}
                  disabled={isExporting || !canCompare}
                >
                  {isExporting
                    ? t("branchAnalysis.compare.export.exporting")
                    : t("branchAnalysis.compare.export.button")}
                </Button>
              </SetupCoachmark>
            ) : (
              <Button
                id="self-setup-branch-compare-export"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleExportPdf}
                disabled={isExporting || !canCompare}
              >
                {isExporting
                  ? t("branchAnalysis.compare.export.exporting")
                  : t("branchAnalysis.compare.export.button")}
              </Button>
            )}
            <Button onClick={onClose}>{t("common.close")}</Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>{t("common.close")}</Button>
            <Button
              variant="contained"
              disabled={!canCompare}
              onClick={() => setStep("compare")}
            >
              {t("branchAnalysis.compare.next")}
            </Button>
          </>
        )}
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default BranchComparisonDialog;
